import {
  getRegionConfig,
  RegionCode,
  formatMobileAccountNumberForRegion,
} from "./regions";
import {
  MOBILE_MONEY_PROVIDERS_BY_REGION,
  type ProviderRecord,
} from "../shared/financial-data";

export function getMobileMoneyProviders(
  region: RegionCode | undefined | null,
): ProviderRecord[] {
  const cfg = getRegionConfig(region);
  return MOBILE_MONEY_PROVIDERS_BY_REGION[cfg.code] ?? [];
}

const PROVIDER_LOOKUP: Map<string, ProviderRecord> = (() => {
  const map = new Map<string, ProviderRecord>();
  const add = (key: string, record: ProviderRecord) => {
    if (!key) return;
    map.set(key.toLowerCase(), record);
  };
  Object.values(MOBILE_MONEY_PROVIDERS_BY_REGION).forEach((providers) => {
    providers.forEach((provider) => {
      add(provider.code, provider);
      add(provider.name, provider);
    });
  });
  return map;
})();

export function getProviderMetadata(
  provider?: string | null,
): ProviderRecord | undefined {
  if (!provider) return undefined;
  return PROVIDER_LOOKUP.get(provider.trim().toLowerCase());
}

const DEFAULT_PHONE_LABEL = "Phone Number";
const DEFAULT_ACCOUNT_LABEL = "Account Number";

export interface MobileIdentifierSource {
  provider?: string | null;
  phoneNumber?: string | null;
  accountNumber?: string | null;
}

export interface MobileIdentifierDetails {
  label: string;
  value: string;
  rawValue: string;
  type: "phone" | "account";
}

export function getMobileProviderIdentifierConfig(
  provider?: string | null,
): {
  type: "phone" | "account";
  label: string;
  placeholder?: string;
} {
  const meta = getProviderMetadata(provider);
  const type = meta?.identifierType ?? "phone";
  return {
    type,
    label: meta?.identifierLabel ??
      (type === "account" ? DEFAULT_ACCOUNT_LABEL : DEFAULT_PHONE_LABEL),
    placeholder: meta?.identifierPlaceholder,
  };
}

export function getMobileMoneyIdentifierDetails(
  source: MobileIdentifierSource,
  region: RegionCode | undefined | null,
): MobileIdentifierDetails {
  const rawSourceValue = source.accountNumber || source.phoneNumber || "";
  const normalized = formatMobileAccountNumberForRegion(region, rawSourceValue);
  const digitsOnly = normalized.replace(/\D/g, "");
  const lastTen = digitsOnly.length > 10 ? digitsOnly.slice(-10) : digitsOnly;
  const displayValue = lastTen || normalized || rawSourceValue;

  return {
    label: DEFAULT_ACCOUNT_LABEL,
    value: displayValue,
    rawValue: rawSourceValue,
    type: "account",
  };
}
